/**
 * @fileoverview Dashboard board card chrome (MOR-906 §7.8)
 *
 * Every card on the board — a built-in metric, a saved widget, a saved table
 * — is draggable and removable, so the chrome that provides both lives here
 * rather than being copied per card kind.
 *
 * Two shapes: a compact tile for a single number, whose controls only appear
 * on hover so a 100px card is not mostly buttons, and a panel with a titled
 * header for anything that draws. Only the panel resizes; a tile is one
 * number, and a wider card would not show it any better.
 *
 * Used by:
 * - src/components/timesheets/FavouriteReportsBoard.tsx
 */

import type { ReactNode } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Maximize2, Minimize2, Pencil, X } from "lucide-react";

import type { BoardCardSize } from "@cvx/timesheets/timesheetProfiles/schema";

import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives";

const CARD_CLASS =
  "min-w-0 overflow-hidden rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]";

/**
 * Board columns are sixths on a wide screen: a metric tile takes one, a
 * chart three, a wide table all six. Narrower screens collapse to halves and
 * thirds, where anything bigger than a tile simply spans the row.
 */
function spanClass(size: BoardCardSize): string {
  switch (size) {
    case "small":
      return "col-span-1";
    case "half":
      return "col-span-2 md:col-span-3 lg:col-span-3";
    case "full":
      return "col-span-2 md:col-span-3 lg:col-span-6";
  }
}

interface CardControls {
  /** Sortable id — "overview:<key>", "widget:<id>" or "table:<id>". */
  id: string;
  title: string;
  size: BoardCardSize;
  onRemove: () => void;
  removeLabel: string;
}

/**
 * A single number: label, value, optional sub-line. Fixed at tile width —
 * there is nothing in one number that a wider card would show better.
 */
export function BoardTileCard({
  id,
  title,
  size,
  onRemove,
  removeLabel,
  value,
  sub,
}: CardControls & { value: string; sub?: string }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(CARD_CLASS, "group relative p-4", spanClass(size))}
    >
      <div className="absolute right-1 top-1 flex items-center opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
        <DragHandle
          title={title}
          attributes={attributes}
          listeners={listeners}
        />
        <RemoveButton onRemove={onRemove} label={removeLabel} />
      </div>
      <p className="text-xs text-muted-foreground">{title}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

/** Anything that draws: a chart, a table, a widget. */
export function BoardPanelCard({
  id,
  title,
  size,
  onResize,
  onRemove,
  removeLabel,
  subtitle,
  onEdit,
  children,
}: CardControls & {
  /** Next width in the card's own cycle; the board decides what that is. */
  onResize: () => void;
  /** Reopen the builder on this card's spec. Absent on built-in cards. */
  onEdit?: () => void;
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(CARD_CLASS, spanClass(size))}
    >
      <div className="flex items-center gap-1.5 border-b border-border/50 px-3 py-2">
        <DragHandle
          title={title}
          attributes={attributes}
          listeners={listeners}
        />
        <span className="truncate text-sm font-semibold">{title}</span>
        {subtitle && (
          <span className="truncate text-xs text-muted-foreground">
            {subtitle}
          </span>
        )}
        <div className="ml-auto flex items-center gap-0.5">
          {onEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={onEdit}
              aria-label={`Edit ${title}`}
              title="Edit"
            >
              <Pencil className="size-3.5" />
            </Button>
          )}
          <ResizeButton size={size} onResize={onResize} />
          <RemoveButton onRemove={onRemove} label={removeLabel} />
        </div>
      </div>
      <div className="min-w-0 overflow-x-auto p-3">{children}</div>
    </div>
  );
}

function DragHandle({
  title,
  attributes,
  listeners,
}: {
  title: string;
  attributes: ReturnType<typeof useSortable>["attributes"];
  listeners: ReturnType<typeof useSortable>["listeners"];
}) {
  return (
    <button
      type="button"
      className="cursor-grab text-muted-foreground/60 hover:text-muted-foreground"
      aria-label={`Reorder ${title}`}
      {...attributes}
      {...listeners}
    >
      <GripVertical className="size-4" />
    </button>
  );
}

function ResizeButton({
  size,
  onResize,
}: {
  size: BoardCardSize;
  onResize: () => void;
}) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-7"
      onClick={onResize}
      aria-label={size === "full" ? "Shrink card" : "Widen card"}
      title={size === "full" ? "Shrink card" : "Widen card"}
    >
      {size === "full" ? (
        <Minimize2 className="size-3.5" />
      ) : (
        <Maximize2 className="size-3.5" />
      )}
    </Button>
  );
}

function RemoveButton({
  onRemove,
  label,
}: {
  onRemove: () => void;
  label: string;
}) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-7 text-muted-foreground hover:text-destructive"
      onClick={onRemove}
      aria-label={label}
      title={label}
    >
      <X className="size-3.5" />
    </Button>
  );
}
